import React from "react";
import { FiShield, FiLock, FiAlertTriangle, FiSearch, FiUserCheck, FiKey, FiServer, FiCode, FiCheckCircle, FiActivity } from "react-icons/fi";
import {
  LuShieldAlert,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuZap,
  LuClipboardList,
  LuSettings,
  LuCode,
  LuBug,
  LuRocket,
  LuActivity,
  LuLock,
  LuCircleCheck,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const securityTestingHeroData = {
  backgroundImage: "/images/serviceBanners/SecurityBanner.webp",
  whiteImage: "/images/serviceBanners/securtiyBW.webp",
  subTitle: "We Create Security Testing Solutions",
  title: (
    <>
      Protect Your Business with <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Security & Penetration Testing Solutions</span>
    </>
  ),
  description:
    "Security and penetration testing solutions designed to identify vulnerabilities, strengthen system defenses, and ensure secure, reliable, and resilient applications that protect business data and maintain long term digital trust.",
  primaryButton: {
    title: "Get Security Audit",
    onClick: () => console.log("Security Start"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("Security Work"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Fast and optimized security testing execution.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Responsive Design",
      desc: "Works across all devices and screen environments.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable",
      desc: "Strong vulnerability detection and secure system validation.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clean Code",
      desc: "Maintainable and structured security testing framework.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

// 2. Stats Block
export const securityStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const securitySolutionsAndProcessData = {
  buildSubtitle: "OUR SECURITY SERVICES",
  buildTitle: "Security Testing Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform Security Testing",
      desc: "Validate security across Android and iOS apps built on multiple technologies.",
      icon: FiActivity,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise Security Testing",
      desc: "Ensure robust protection for large-scale business applications and systems.",
      icon: FiSearch,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Security Testing",
      desc: "Test payment flows, data security, and user protection in online stores.",
      icon: FiCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand App Security",
      desc: "Secure real-time booking and service-based applications from vulnerabilities.",
      icon: FiUserCheck,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Security Testing",
      desc: "Ensure compliance and protection for complex financial data and transactions.",
      icon: FiSearch,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom Security Testing Solutions",
      desc: "Tailored security testing strategies based on specific business requirements.",
      icon: FiCode,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "SECURITY TESTING PROCESS",
  processTitle: "Security Testing Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand security requirements, business risks, and application scope.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define security testing approach, tools, and vulnerability coverage plan.",
      icon: LuActivity,
    },
    {
      step: "03",
      title: "Test Design",
      description: "Create penetration test cases and security validation scenarios.",
      icon: LuBug,
    },
    {
      step: "04",
      title: "Security Testing",
      description: "Identify vulnerabilities in applications, APIs, and network system layers.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Risk Analysis",
      description: "Evaluate threats, classify active risks, and suggest remediation steps.",
      icon: LuCircleCheck, // Use a build-compliant icon to circumvent compilation errors
    },
    {
      step: "06",
      title: "Deployment & Reporting",
      description: "Deliver security reports and support fixes for final production readiness.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR SECURITY TESTING SERVICES?",
  benefitsTitle: "Why Businesses Choose Security Testing Solutions",
  benefits: [
    "Cross-platform support",
    "Faster development cycles",
    "Modern UI validation",
    "Smooth performance testing",
    "Efficient workflow system",
    "Scalable security testing",
    "Cost-effective security QA",
  ],
  logoImage: "/images/serviceBanners/SecurityBanner.webp",
  whiteLogoImage: "/images/serviceBanners/securtiyBW.webp",
};

// 4. Portfolio Data
export const securityPortfolioData = {
  subtitle: "CASE STUDIES",
  title: "Secure Architectures at Scale",
  buttonText: "View Security Work",
  buttonLink: "#",
  apps: [
    {
      title: "BankShield Enterprise",
      badge: "Security Audit",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Comprehensive penetration testing for a neo-banking app, identifying 12 critical API flaws.",
      downloads: "Active",
      rating: "5.0",
      image: "/images/Devops_cloud/security1.jpg",
    },
  ],
};

// 5. Technologies Data
export const securityTechnologiesData = {
  techTitle: "OUR SECURITY STACK",
  technologies: [
    { name: "Burp Suite", icon: "RestAPI" },
    { name: "Metasploit", icon: "Code" },
    { name: "OWASP ZAP", icon: "Vite" },
    { name: "Nessus", icon: "Firebase" },
    { name: "Wireshark", icon: "ReactNative" },
    { name: "Kali Linux", icon: "Dart" },
  ],
  trustTitle: "TRUSTED BY SECURITY-CONSCIOUS BRANDS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

// 6. About Overview
export const securityAboutData = [
  {
    title: "Security Testing Services for Stronger Digital Protection",
    content: (
      <>
        Modern businesses face growing cybersecurity risks that can impact customer trust, business continuity, and operational performance. Reliable{" "}
        <b className="text-text-primary font-semibold">security testing services</b> help organizations identify vulnerabilities, reduce security gaps, and strengthen digital
        systems before threats become serious issues. Many businesses struggle with hidden risks that remain unnoticed until systems are compromised. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, security frameworks are designed around business requirements, risk management, and long-term
        protection to help organizations create secure and dependable digital environments.
      </>
    ),
  },
  {
    title: "Cybersecurity Testing Company for Better Risk Prevention",
    content: (
      <>
        Protecting applications requires more than basic monitoring and preventive measures. A trusted{" "}
        <b className="text-text-primary font-semibold">cybersecurity testing company</b> helps businesses uncover hidden weaknesses, validate system security, and strengthen
        digital defenses against evolving cyber threats. Companies investing in <b className="text-text-primary font-semibold">application security testing</b> often improve
        reliability because vulnerabilities are identified before they affect customers or operations. Tycho Technologies focuses on practical security assessments designed to
        improve business resilience while maintaining strong operational performance.
      </>
    ),
  },
  {
    title: "Penetration Testing Services for Smarter Threat Detection",
    content: (
      <>
        Businesses managing sensitive customer information often require deeper visibility into system weaknesses and potential attack points. Reliable{" "}
        <b className="text-text-primary font-semibold">penetration testing services</b> help organizations evaluate digital defenses, strengthen infrastructure, and improve
        response readiness against cyber risks. Companies aiming to improve application reliability frequently combine security assessments with{" "}
        <a href="https://tychotechnologies.com/performance-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          performance testing services
        </a>{" "}
        to ensure systems remain stable, responsive, and protected under demanding conditions.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Security Testing Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">security testing company in india</b> continues to grow as businesses prioritize stronger protection
        against digital threats and compliance challenges. Companies investing in <b className="text-text-primary font-semibold">cybersecurity services india</b> often focus on
        long-term system security, operational continuity, and customer trust. Businesses partnering with Tycho Technologies gain access to customized security strategies designed
        around measurable risk reduction and technical requirements. Many organizations also strengthen product reliability through{" "}
        <a href="https://tychotechnologies.com/automated-regression-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          automated regression testing services
        </a>{" "}
        to maintain secure and stable software ecosystems.
      </>
    ),
  },
];
// 7. FAQs (Preserved)
export const securityTestingFaqs = [
  {
    q: "What is penetration testing?",
    a: "Penetration testing simulates real-world attacks to identify and exploit vulnerabilities, helping you strengthen your defenses before attackers do.",
  },
  {
    q: "Will testing disrupt our operations?",
    a: "Our ethical hackers coordinate closely with your team to minimize risk and avoid disruption during testing.",
  },
];
